import type { Customer } from '@brighterly/lib-core-types/customer/interfaces'

import { useCustomerStore } from '@stores/customer'
import { useCommonStore } from '@stores/common'

import { useAdminRoutes } from '@composables/routes/admin-route.hook'

import { getAxiosErrorMessage } from '@helpers/axios'

import { useToast } from '@UI/Toast/ToastProvider'

interface CustomerShowApiOptions {
  customerId: string | number
  /** Leave the layout loader alone: the caller shows its own (e.g. the discipline page loader). */
  silent?: boolean
  onStart?: () => void
  onSuccess?: (data: Customer) => void
  onError?: (err: unknown) => void
  onFinish?: () => void
}

export const useCustomerShowApi = async (options: CustomerShowApiOptions): Promise<Customer | null> => {
  const { $axios } = useNuxtApp()
  const toast = useToast()

  const url = useAdminRoutes('admin.customers.show', { customer_id: options.customerId })

  const customerStore = useCustomerStore()
  const commonStore = useCommonStore()

  const setLoading = (value: boolean): void => {
    if (!options.silent) commonStore.setLoading(value)
  }

  options.onStart?.()
  setLoading(true)

  try {
    const { data } = await $axios.get(url!)
    const customer: Customer = data.data

    customerStore.setCustomer(customer)
    setLoading(false)

    options.onSuccess?.(customer)

    return customer
  } catch (err: unknown) {
    console.error(err)
    toast.error(getAxiosErrorMessage(err))
    setLoading(false)
    options.onError?.(err)

    return null
  } finally {
    options.onFinish?.()
  }
}